import { Competitor } from "./Competitors/types";
import { EVENT_TYPES } from "./consts";
import { CompetitorPaymentWithMetadata } from "../Api/payments";

export type ColorSchema = "default" | "redblue" | "bluered" | "whiteblue" | "redwhite";

export type Mat = {
    id: number;
    eventId: number;
    name: string;
};

// Announcer board + Scoreboards
export type CalledCompetitor = {
    time: number;
    competitor1: Competitor;
    competitor2: Competitor;
    fightId: number;
    matId: number;
    category: string;
    calledCompetitorIds: number[]; // [id] = one missing; [id1,id2] = both missing
};

export type ContactInfo = {
    email1: string | null;
    phone1: string | null;
};

export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_STATUS = {
    Draft: 0,
    RegistrationBySchedule: 1,
    RegistrationOpen: 2,
    Archived: 3,
};

export type EventStatus = (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS];

export const EVENT_VISIBILITY = {
    Public: 0,
    Private: 1,
    None: 2,
};

export type Visibility = (typeof EVENT_VISIBILITY)[keyof typeof EVENT_VISIBILITY];

export type Event = {
    id: number;
    fullName: string;
    shortName: string;
    urlSlug: string;
    place: string;
    eventDate: EventDate;
    status: EventStatus;
    visibility: Visibility;
    thumbnailImage: string | null;
    backgroundImage: string | null;
    registrationStart: string;
    registrationEnd: string;
    internalInformation: string | null;
    externalInformation: string | null;
    translation: string | null;
    registrationStages: Array<string>;
    timezone: string;
    geoLocation: string | null;
    contactInfo: ContactInfo | null;
    country: string | null;
};

export type EventMetadata = {
    canChangeStatus?: boolean;
    federationApproved?: boolean;
    federationId?: string | null;
    federationBillingDetails?: {
        companyName: string;
        street: string;
        city: string;
        postalCode: string;
        country: string;
        taxId: string;
    } | null;
    postWizardOnboardingDecision?: string | null;
    isUnderReview?: boolean;
};

export type FullEvent = {
    event: Event;
    eventTypes: EventType[];
    metadata?: EventMetadata;
};

export type CompactEvent = {
    id: number;
    fullName: string;
    shortName: string;
    eventDate: EventDate;
    timezone: string;
    canMoveToFederation: boolean;
    federationId: string | null;
    thumbnailImage: string | null;
    isPrivate: boolean;
};

export type EventDate = {
    startDate: string;
    endDate: string;
};

export type Category = {
    key: number;
    value: string;
};

export type EventBillingInfo = {
    eventId: number;
    chargingCurrency: string;
};

export type SupportReaction = {
    id: string;
    created: string;
    description: string;
    price: {
        value: number;
        currency: string;
    };
};

export type UserRole = "organizer" | "admin" | "techAccount";

export type User = {
    id: number;
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
    role: UserRole | null;
    active: boolean;
    created: string;
    academy: Academy | null;
    branch: string;
};

export type Academy = {
    id: number;
    name: string;
};

export type FullUser = Omit<User, "active"> & { academy: Academy } & {
    branch: string | null;
    phone: string;
    isActive: boolean;
};

export type InvitationLink = {
    id: string;
    eventId: number;
    created: string;
    userId: number | null;
    expires: string | null;
};

export const BracketTypeEnum = {
    QueueBreak: -1,
    NotSupported: 0,
    SingleElimination: 1,
    ThreeCompetitorRepechage: 2,
    SingleElimination3rdPlacePlayoff: 3,
    RoundRobin: 4,
    DoubleEliminationTwo3rdPlaces: 5,
    DoubleEliminationOne3rdPlace: 7,
    TwoPoolsTwo3rdPlaces: 6,
    TwoPoolsOne3rdPlace: 8,
    BestOfThree: 9,
    Kata: 10,
    RepechageByFinalists: 11,
} as const;

export type BracketType = (typeof BracketTypeEnum)[keyof typeof BracketTypeEnum];

export type MultiFieldValidationErrors = Record<string, string[]>;

export type DefaultValidationError = {
    field: string;
    message: MultiFieldValidationErrors;
};

export type SideEffectType =
    | "createBracket"
    | "updateBracket"
    | "deleteBracket"
    | "missingRegistrationInFights"
    | "unexpectedRegistrationInFights";

export type EventRegistrationSideEffectReport = {
    sideEffects: EventRegistrationSideEffect[];
    eventId: number;
    registeredCompetitor: CompetitorPaymentWithMetadata;
};

export type DeleteRegistrationPreviewStatus = "canDelete" | "canDeleteWithWarnings" | "cannotDelete" | "cannotDeletePaymentBlocked";

export type EventRegistrationSideEffect = {
    parameterId: number;
    categoryName: string;
    type: SideEffectType;
    fixBlocked: boolean;
    canBeIgnored: boolean;
};

export const BracketSeedingTypeEnum = {
    ByRankingAndAcademy: "BY_RANKING_AND_ACADEMY",
    ByAcademy: "BY_ACADEMY",
    ByAcademyRandomized: "BY_ACADEMY_RANDOMIZED",
    ByAssignedSpot: "BY_ASSIGNED_SPOT",
};

export type BracketSeedingType = (typeof BracketSeedingTypeEnum)[keyof typeof BracketSeedingTypeEnum];

export type BracketSeedCompetitor = {
    id: number;
    firstName: string;
    lastName: string;
    academy: string;
    branch: string;
    parameterId: number;
    nationality: string | null;
    uniqueId: string;
};

export type BracketColorOption = "1/2" | "1/4" | "1/8";

//Validation

export type ValidationErrorsMap = Record<string, string[]>;
export type ValidationError = { field: string; message: string[] };
